// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { Link } from 'react-router-dom';
import { FormattedMessage, intlShape } from 'react-intl';
import Loading from '../../../../elements/Loading/Loading';
import Modal from '../../../../elements/Modal/Modal';
import type { Match } from '../../../../router';
import { getFullAddress, getFullAdmin } from '../../../../utils/address';
import { Default, Mobile } from '../../../../utils/responsive';
import { getPaymentInfo } from '../../CheckoutStep/utils';
import PunchCardContainer from '../../../punch-card/containers/PunchCardContainer';
import style from './CheckoutSuccessStep.scss';
import orderCommonStyle from '../../../order/components/OrderCommon.scss';
import SingleProduct, {
  SingleProductButtonContent,
  SingleProductData,
} from '../../../../elements/SingleProduct/SingleProduct';
import { groupItems } from '../../QuoteStep/utils';
import moment from 'moment';

import buttonStyle from '../../../../assets/styles/button.scss';
import icons from '../../../../assets/styles/icons.scss';
import yogiLeg from '../../../../assets/img/YOGI-QEMPO2.png';
import fblogo from '../../../../assets/img/new-order/checkout-success-step/facebook.png';
import glogo from '../../../../assets/img/new-order/checkout-success-step/google.png';

import type { HTTPError } from '../../../../error';
import type { Order, OrderItem } from '../../../../entities';
import type { ItemsByStore } from '../../QuoteStep/components/QuoteSummary/QuoteSummary';
import type { Node } from 'react';
import type { LocaleState } from '../../../locale/duck';
import type { LocalValuesState } from '../../../local-values/duck';

type Props = {
  +order: Order;
  +goToRedirect: (route: string) => void;
  +resetState: () => void;
  +fetchOrder: (idOrder: string) => void;
  +isFetchingOrder?: boolean;
  +fetchOrderError?: HTTPError;
  +match: Match;
  +locale: LocaleState;
  +localValues: LocalValuesState;
};

export default class CheckoutSuccessStep extends Component<Props, void> {
  componentWillUnmount() {
    const { resetState } = this.props;
    if (resetState) resetState();
  }

  componentDidMount() {
    const { order, fetchOrder, match } = this.props;
    if (!order) {
      fetchOrder(match.params.orderId);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { fetchOrderError, goToRedirect } = this.props;
    if (!prevProps.fetchOrderError && !!fetchOrderError) {
      goToRedirect('/');
    }
  }

  buildPaymentMethod() {
    const { order } = this.props;
    if (order && order.invoice) {
      const image = getPaymentInfo(order.invoice) || '';
      return (<Fragment>
        <img src={image} alt="Qempo"/>
        {order.invoice && order.invoice.payment && order.invoice.payment.paymentData
        && order.invoice.payment.paymentData.lastDigits &&
        <p>{order.invoice.payment.paymentData.lastDigits}</p>}
      </Fragment>);
    }
  }

  handleClickSharedDialog = () => {
    if (!(window: any).FB) return Promise.reject(new Error('FB is not defined'));
    (window: any).FB.ui({
      method: 'share',
      href: 'https://www.qempo.com',
    }, (response) => console.log(response));
  };

  buildProducts(itemsByStores: ItemsByStore[]): Node {
    const { locale, localValues } = this.props;
    return (
      <Fragment>
        {itemsByStores.map((store: ItemsByStore, n: number) =>
          <div key={n}>
            <div className={orderCommonStyle.storeInfoWrap}>
              <FormattedMessage id="newOrder.quote.products.soldBy">{
                txt => <p className={orderCommonStyle.soldBy}>{txt}: {store.name}</p>
              }</FormattedMessage>
            </div>
            {store.items.map((item: OrderItem) => {
              return (
                <Fragment key={item.index}>
                  <SingleProduct localeState={locale}
                                 index={item.index}
                                 item={item}
                                 userType="purchaser"
                                 buttonChildren={<SingleProductButtonContent type='VIEW'
                                                                             amount={item.amount}
                                                                             name={item.name}
                                                                             image={item.image}
                                                                             imageURI={item.imageURI}/>
                                 }>
                    <SingleProductData
                      categoryId={{
                        intl: 'singleProduct.categoryId.label',
                        content: localValues.categoriesMap ?
                          (localValues.categoriesMap: any)[`${item.itemCategoryId}`]
                            .translations[locale.locale.toUpperCase()].name : '',
                      }}
                      detail={{ content: item.detail }}
                      originalPackaging={{
                        intl: 'singleProduct.packaging.label',
                        content: item.originalPackaging ?
                          this.context.intl.formatMessage({ id: 'singleProduct.packaging.withBox' }) :
                          this.context.intl.formatMessage({ id: 'singleProduct.packaging.withoutBox' }),
                      }}
                      price={{ intl: 'singleProduct.price.label', content: item.price }}
                      total={{
                        intl: 'singleProduct.total.label',
                        content: (parseFloat(item.price) * item.amount).toFixed(2),
                      }}
                      url={{ intl: 'singleProduct.url.label', content: item.url }}
                      weight={{ intl: 'singleProduct.weight.label', content: item.weight }}>
                    </SingleProductData>
                  </SingleProduct>
                </Fragment>);
            })}
          </div>)}
      </Fragment>
    );
  }

  buildDateAndTime = (): ?Node => {
    const { order, goToRedirect } = this.props;
    if (!order) return;
    let formattedDate;
    if (
      order.invoice.payment &&
      order.invoice.payment.state !== 'PENDING' &&
      order.invoice.payment.method === 'NIUBIZ_WEB'
    ) {
      const date = order.invoice.payment.paymentData.date;
      formattedDate = `20${date.substr(0, 2)}-${date.substr(2, 2)}-${date.substr(4, 2)} 
    ${date.substr(6, 2)}:${date.substr(8, 2)}`;
      return (
        <div>
          <FormattedMessage id="newOrder.checkoutSuccess.box.detail.purchaseTime">{
            text => <p className={style.title}>{text}</p>
          }</FormattedMessage>
          <p>
            {moment(Date.parse(formattedDate)).format('DD MMM YYYY, HH:mm:ss')}
          </p>
        </div>);
    } else {
      goToRedirect(`/new-order/checkout/order/${order.id}`);
    }
  }

  render() {
    const { order, isFetchingOrder } = this.props;
    let fullName, fullAddress, idNumber, orderId = '';
    if (order && order.shippingAddress) {
      fullAddress = `${getFullAdmin(order.shippingAddress)}, ${getFullAddress(order.shippingAddress)}`;
      fullName = `${order.shippingAddress.firstName} 
                  ${order.shippingAddress.lastName}`;
      idNumber = order.shippingAddress.idNumber;
    }
    if (order) {
      orderId = order.id;
      if (order.invoice.payment.method === 'NIUBIZ_WEB') {
        orderId = parseInt(order.id.substr(4));
      }
    }
    const itemsByStores: ItemsByStore[] = groupItems(order ? order.items : []);
    return (
      <Fragment>
        <section className={style.checkoutSuccessStep}>
          <section className={style.leftBlock}>
            <img src={yogiLeg} alt="Qempo"/>
            <FormattedMessage id="newOrder.checkoutSuccess.title">{
              text => <h2 className={style.title}>{text}</h2>
            }</FormattedMessage>
            <FormattedMessage id="newOrder.checkoutSuccess.subtitle.one">{
              text => <p className={style.subtitle}>{text}</p>
            }</FormattedMessage>
            <FormattedMessage id="newOrder.checkoutSuccess.subtitle.two">{
              text => <p className={style.subtitle}>{text}: <strong>{orderId}</strong></p>
            }</FormattedMessage>
            <Default>
              <section className={style.buttonsBox}>
                <FormattedMessage id="newOrder.checkoutSuccess.button.keepBuying">{
                  text => <Link to="/" className={classNames(buttonStyle.btn, buttonStyle.lightBlueInverse)}>
                    <i className={icons.iconShoppingCartMedium}/>{text}</Link>
                }</FormattedMessage>
                <FormattedMessage id="newOrder.checkoutSuccess.button.myOrders">{
                  text => <Link to="/orders/active"
                                className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}>{text}</Link>
                }</FormattedMessage>
              </section>
            </Default>
            <FormattedMessage id="newOrder.quote.products.title">{
              txt => <h2 className={style.productTitle}>{txt}</h2>
            }</FormattedMessage>
            {this.buildProducts(itemsByStores)}
          </section>
          <section className={style.rightBlock}>
            <FormattedMessage id="newOrder.checkoutSuccess.title.share">{
              text => <p>{text}</p>
            }</FormattedMessage>
            <section onClick={this.handleClickSharedDialog}
                     className={classNames(style.socialBlock, style.box)}>
              <FormattedMessage id="newOrder.checkoutSuccess.box.social.title.sharing">{
                text => <p>{text}</p>
              }</FormattedMessage>
              <img src={fblogo} alt="facebook"/>
            </section>
            <a href="https://www.google.com/search?q=Qempo#lrd=0x9105c88b533d38cd:0x85771c5ac4165f4b,3"
               target="_blank" className={classNames(style.socialBlock, style.box)}>
              <FormattedMessage id="newOrder.checkoutSuccess.box.social.title.commentary">{
                text => <p>{text}</p>
              }</FormattedMessage>
              <img src={glogo} alt="google"/>
            </a>
            <FormattedMessage id="newOrder.checkoutSuccess.title.details">{
              text => <p>{text}</p>
            }</FormattedMessage>
            <section className={classNames(style.detailBlock, style.box)}>
              <FormattedMessage id="newOrder.checkoutSuccess.box.detail.subtitle">{
                text => <p className={style.title}>{text}</p>
              }</FormattedMessage>
              <p>{fullName}</p>
              <FormattedMessage id="newOrder.checkoutSuccess.box.detail.dni">{
                text => <p>{`${text}: ${idNumber}`}</p>
              }</FormattedMessage>
              <p>{fullAddress}</p>
              <FormattedMessage id="newOrder.checkoutSuccess.box.detail.order">{
                text => <p className={style.title}>{text}</p>
              }</FormattedMessage>
              <p>{order && order.id}</p>
              {this.buildDateAndTime()}
            </section>
            <section className={classNames(style.paymentMethod, style.box)}>
              <FormattedMessage id="newOrder.checkoutSuccess.box.payment.title">{
                text => <p>{text}</p>
              }</FormattedMessage>
              <section className={style.detail}>
                {this.buildPaymentMethod()}
              </section>
            </section>
            <FormattedMessage id="newOrder.checkoutSuccess.total">{
              text => <p className={style.total}>{`${text} $${order && order.invoice
              && order.invoice.payment && order.invoice.payment.total}`}</p>
            }</FormattedMessage>
            {/*<article className={style.punchCardWrap}>
              <PunchCardContainer/>
            </article>*/}
          </section>
          <Mobile>
            <section className={style.buttonsBox}>
              <FormattedMessage id="newOrder.checkoutSuccess.button.keepBuying">{
                text => <Link to="/" className={classNames(buttonStyle.btn, buttonStyle.lightBlueInverse)}>
                  <i className={icons.iconShoppingCartMedium}/>{text}</Link>
              }</FormattedMessage>
              <FormattedMessage id="newOrder.checkoutSuccess.button.myOrders">{
                text => <Link to="/orders/active"
                              className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}>{text}</Link>
              }</FormattedMessage>
            </section>
          </Mobile>
        </section>
        <Modal handleClose={() => undefined}
               open={isFetchingOrder}
               transparent
               maskColor="white"
               showCloseButton={false}>
          <Loading type="circle"
                   className={style.loading}/>
        </Modal>
      </Fragment>
    );
  }
}

CheckoutSuccessStep.propTypes = {
  order: PropTypes.object,
  goToRedirect: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
  fetchOrder: PropTypes.func.isRequired,
  isFetchingOrder: PropTypes.bool,
  fetchOrderError: PropTypes.object,
  match: PropTypes.object.isRequired,
  locale: PropTypes.object.isRequired,
  localValues: PropTypes.object.isRequired,
};

CheckoutSuccessStep.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/new-order/CheckoutSuccessStep/components/CheckoutSuccessStep.jsx